import { useEffect, useState } from "react";
import Select from "react-select/dist/declarations/src/Select";
import { ContractRequest, NewContractSaveModel } from "src/api/contract/contract-types";
import { ApplicationFormModel, ProductModel } from "src/api/product/product-types";
import { BaseButton } from "src/components";
import EditCancelButton from "src/components/EditCancelButton";
import useNavigate from "src/hooks/usePartnerNavigate";
import pagePath from "src/pagePath.json";
import ContractorInfo from "src/pages/contract/commonComponents/ContractorInfo";
import ContractProductEdit from "src/pages/contract/commonComponents/editComponent/ContractProductEdit";
import useContractApi from "src/pages/contract/hooks/useContractApi";
import { useS2Contract } from "src/pages/contract/hooks/useS2Contract";
import { PagePath } from "src/pages/product/details";
import useUsageApplication from "src/pages/product/hooks/useUsageApplication";
import { useModal } from "src/recoil/modalState/hook";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useToast } from "src/recoil/toast/hook";
import { formatDateTimeRange } from "src/utils";
import FOType from "./component/FOType";
import ProductCommomFacilitySelect from "./component/ProductCommomFacilitySelect";
import ServiceType from "./component/ServiceType";
import UsageApplicationForm from "./component/UsageApplicationForm";

const ContractApplication = () => {
  const navigate = useNavigate();

  const { isS2Contract } = useS2Contract();
  const { isAuthority } = usePartnerAuthority();
  const { openToast } = useToast();
  const { createContractData, sendQuoTation } = useContractApi();
  const { getPrApplication } = useUsageApplication();

  const [product, setProduct] = useState<ProductModel>();
  const [selectedPeriodOption, setSelectedPeriodOption] = useState<Select[]>([]);

  // 이용신청서 파일 리스트

  const { setBaseModal, setAbstractModalZ1 } = useModal();

  const [completedApplicationForm, setCompletedApplicationForm] = useState<ApplicationFormModel>();
  const [saveData, setSaveData] = useState<NewContractSaveModel>({
    mbOrganizationId: 0,
    productId: 0,

    contractApplyType: "CONTRACT",
    useStartTime: "",
    useEndTime: "",

    applicantName: "",
    applicantCorporation: "",
    isAgreeTerms: true,

    applicantMobile: "",
    applicantMemberNo: "",
  });

  const validationCheck = (saveData: any) => {
    let result = true;

    if (saveData.productId === undefined || Number(saveData.productId) === 0) {
      openToast({ content: "상품 정보는 필수 입력 항목입니다.", isSystemToast: true });
      result = false;
      return result;
    }
    if (saveData.applicantMemberNo === undefined || saveData.applicantMemberNo === "") {
      openToast({ content: "신청자 정보는 필수 입력 항목입니다.", isSystemToast: true });
      result = false;
      return result;
    }
    if (product?.productType === "TIME_COURT") {
      if (saveData.selectedFacility === undefined) {
        openToast({ content: "공용공간 선택은 필수 입니다.", isSystemToast: true, duration: 2000 });
        result = false;
        return result;
      }
    }
    if (saveData.useStartTime === undefined || saveData.useStartTime === "") {
      openToast({ content: "이용 시작일은 필수 입력 항목입니다.", isSystemToast: true });
      result = false;
      return result;
    }
    if (saveData.useEndTime === undefined || saveData.useEndTime === "") {
      openToast({ content: "이용 시작일은 필수 입력 항목입니다.", isSystemToast: true });
      result = false;
      return result;
    }
    if (product?.productType && product?.productType !== "TIME_COURT") {
      if (Number(saveData.useTerm) === 0) {
        openToast({ content: "기간은 필수 입력 항목입니다.", isSystemToast: true });
        result = false;
        return result;
      }
    }

    return result;
  };

  const onSaveContract = () => {
    setBaseModal({
      isOpen: true,
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
      title: "저장하시겠습니까?",
      onClick: () => {
        onCreateContract(saveData);
        setBaseModal({ isOpen: false });
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
    });
  };

  // 신청계약 저장 api
  const onCreateContract = async (saveData: any) => {
    let defaultPrApplication;

    //견적서 생성 요청을 위한 API
    if (product?.productType === "TIME_COURT") {
      const sendData = {
        quotationItemList: [
          {
            productId: saveData.productId,
            buildingCommonFacilityId: saveData?.selectedFacility!.id!,
            startDate: saveData.useStartTime,
            endDate: saveData.useEndTime,
          },
        ],
      };

      const saveQuotation = await sendQuoTation(sendData);

      if (saveQuotation) {
        saveData.quotationId = saveQuotation.id;
      }
      // 공간예약이고, 이용신청서 사용 여부가 true일 경우 이용신청서 데이터 가져오기
      if (product?.isUseApplication) {
        defaultPrApplication = await getPrApplication({ productId: product?.id.toString() }).then((res) => res?.applicationForm.formData);
      }
    }

    saveData.mbOrganizationId = 0;
    saveData.contractApplyType = "CONTRACT";
    saveData.isAgreeTerms = true;
    delete saveData.useTerm;

    // 이용신청서 저장 포맷 > v.1.31 에 적용 > harry 수정
    const newContractRequest: ContractRequest = {
      productId: saveData.productId,
      applicationHeader: {
        mbOrganizationId: 0,
        memberNo: saveData.applicantMemberNo,
        quotationId: saveData.quotationId,
      },
      applicationBody: {
        isAgreeTerms: true,
        applicantName: saveData.applicantName || "",

        ...(product?.productType !== "TIME_COURT" && { useStartTime: saveData.useStartTime, useEndTime: saveData.useEndTime }),

        // 이용신청서 데이터 가져오기
        ...(product?.isUseApplication && {
          formData: {
            attachmentConfig: completedApplicationForm?.attachmentConfig
              ? completedApplicationForm?.attachmentConfig
              : defaultPrApplication?.attachmentConfig,
            richText:
              product?.isUseApplication && completedApplicationForm?.richText
                ? completedApplicationForm?.richText
                : defaultPrApplication?.richText || "",
          },
          attachments:
            completedApplicationForm?.attachments?.map((item) => {
              const { blobUrl, ...attachmentWithoutBlob } = item;
              return {
                ...attachmentWithoutBlob,
                serviceType: "CT_APPLICATION",
                serviceTag: "APPLICATION_ATTACHMENT",
                isPrimary: false,
                cmdType: "U",
              };
            }) || [],
        }),
      },
    };

    const contractResult = await createContractData(newContractRequest);

    if (contractResult) {
      const savedContractId = contractResult.contractId;

      //s2 전용메뉴 일 경우

      if (isS2Contract) {
        navigate(pagePath.contractS2.detail.replace(":id", String(savedContractId)));
      } else {
        navigate(pagePath.contract.detail.replace(":id", String(savedContractId)));
      }
    }
  };

  const makePeriodOptions = (product: any) => {
    let arr: any = [];

    if (product.productType === "FULL_COURT") {
      const min = Number(product.minLeasePeriod);
      const max = Number(product.maxLeasePeriod);
      for (let i = min; i <= max; i++) {
        const option = { label: i + "개월", value: i };
        arr.push(option);
      }
    }

    if (product.productType === "NON_RESIDENT_OFFICE") {
      const min = Number(product.minLeasePeriod);
      const max = Number(product.maxLeasePeriod);
      for (let i = min; i <= max; i++) {
        const option = { label: i + "개월", value: i };
        arr.push(option);
      }
    }

    setSelectedPeriodOption(arr);

    return arr;
  };
  useEffect(() => {
    if (product) {
      makePeriodOptions(product);
    }
  }, [product]);
  // 이용신청서 모달 오픈
  const openApplicationModal = async () => {
    const applicationFormData = completedApplicationForm
      ? { formData: completedApplicationForm }
      : (await getPrApplication({ productId: product?.id.toString() || "" }))?.applicationForm;
    const applicationSummary = {
      productInfo: {
        spaceProductId: product?.id!,
        spaceProductName: product?.productName!,
        spaceProductType: product?.productType!,
        facilityName: saveData.selectedFacility?.facilityName!,
        useStartEndTime: formatDateTimeRange(saveData.useStartTime, saveData.useEndTime, "S2"),
      },
      contractorInfo: {
        memberNo: saveData.applicantMemberNo!,
        memberEmail: saveData.email!,
        memberPhoneNumber: saveData.applicantMobile!,
        memberName: saveData.applicantName!,
      },
    };

    setAbstractModalZ1({
      isOpen: true,
      children: (
        <UsageApplicationForm //
          fileList={completedApplicationForm?.attachments || []}
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          applicationFormData={applicationFormData}
          applicationSummary={applicationSummary}
          isUseApplication={product?.isUseApplication}
          onSaveFormData={(data: ApplicationFormModel) => {
            console.log("data", data);
            if (data.attachmentConfig?.isRequired && (!data.attachments || data.attachments?.length === 0)) {
              setBaseModal({
                isOpen: true,
                title: "첨부파일 등록은 필수입니다.",
                btnRightTitle: "확인",
                onClick: () => {
                  setBaseModal({ isOpen: false });
                },
              });
              return;
            }
            setCompletedApplicationForm(data);
            setAbstractModalZ1({ isOpen: false });
          }}
        />
      ),
    });
  };
  return (
    <>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          <ContractProductEdit saveData={saveData} saveObject={setSaveData} product={product as ProductModel} setProduct={setProduct} showYn={true} />
          <ContractorInfo detailData={{}} detailFlag={false} saveData={saveData} setSaveData={setSaveData} />

          {(product?.id === undefined || product?.productType === "FULL_COURT" || product?.productType === "NON_RESIDENT_OFFICE") && (
            <FOType saveData={saveData} setSaveData={setSaveData} product={product} selectedPeriodOption={selectedPeriodOption} />
          )}
          {product?.productType === "TIME_COURT" && (
            <>
              <ProductCommomFacilitySelect saveObject={saveData} setSaveData={setSaveData} />
              <ServiceType saveData={saveData} setSaveData={setSaveData} />
            </>
          )}

          <article className="contents-container__wrap-article">
            <div className="contents-container__sub-title">
              <h2>이용신청 정보</h2>
            </div>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>이용신청서</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="">
                  <div className="flex-center">
                    <BaseButton
                      title={completedApplicationForm?.richText ? "변경" : "등록"}
                      className="color-white"
                      onClick={() => openApplicationModal()}
                      disabled={
                        product === undefined || //상품이 없을 경우
                        product?.productType !== "TIME_COURT" || //상품이 TIME_COURT가 아닐 경우
                        product?.isUseApplication === false || //상품이 이용신청서 사용 여부가 false일 경우
                        !saveData.applicantMemberNo || //신청자 정보가 없을 경우
                        !saveData.selectedFacility || //공용공간이 선택되지 않았을 경우
                        !saveData.useStartTime || //이용 시작일이 없을 경우
                        !saveData.useEndTime //이용 종료일이 없을 경우
                      }
                    />
                    <span className="ml10">{completedApplicationForm?.richText && "등록완료"}</span>
                  </div>
                </div>
              </div>
            </section>
          </article>
        </div>
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area"></div>
        <div className="right-area">
          <EditCancelButton to={isS2Contract ? PagePath.contractS2.list : PagePath.contract.list} />
          {(product?.productType !== "TIME_COURT" || isAuthority("w", "BASIC")) && (
            <BaseButton
              title="저장"
              className="size-large"
              onClick={() => {
                const isValid = validationCheck(saveData);
                if (isValid) {
                  // setConfirm(true);
                  onSaveContract();
                }
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ContractApplication;
